/**
 * White-label theme defaults.
 *
 * Brand name / logo / title are overridden by `APP_*` env vars
 * (`src/config/appEnv.ts`). Colors, fonts, and buttons stay here.
 *
 * Tokens follow `rockzy_design/tokens.css` (Figma "Rockzy - Experts Feature").
 * Deep navy #0c2a45, #f6f6f6 canvas, Satoshi throughout.
 * Applied at startup by `applyTheme()` in `main.tsx`.
 */

import { appEnv } from "@/config/appEnv";

const defaultThemeConfig = {
  brand: {
    name: "Rockzy",
    appTitle: "Rockzy Expert Portal",
    logoSrc: "/rockzy-logo.png",
    logoAlt: "Rockzy",
    faviconSrc: "/favicon.png",
    reportName: "Rockzy",
    portalLabels: {
      expert: "Expert Portal",
      client: "Client Portal",
    },
    panelSubtitle: "Expert Portal",
  },

  fonts: {
    /** CSS font-family for body UI (Figma = Satoshi) */
    sans: '"Satoshi", "Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
    /** Headings / display — Figma uses Satoshi 900, no separate serif face */
    display: '"Satoshi", "Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
    mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    /** Stylesheet for the brand face — Satoshi ships from Fontshare, not Google */
    googleFontsUrl:
      "https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700,900&display=swap",
  },

  colors: {
    canvas: "#f6f6f6",
    surface: "#ffffff",

    primary: "#0c2a45",
    primaryHover: "#092138",
    primaryActive: "#061a2e",
    primarySoft: "#e9eff5",

    secondary: "#51a2ff",
    secondaryHover: "#3b82f6",
    secondaryActive: "#2563eb",
    secondarySoft: "#eef6ff",

    text: "#111111",
    textMuted: "#5d5d5d",
    border: "#e7e7e7",
    inputBg: "#ffffff",
    inputBorder: "#e7e7e7",

    logoBg: "#0c2a45",
    logoAccent: "#fdc700",
    successRing: "#ebfff2",
    successCheck: "#16a34a",

    expertSidebar: "#0c2a45",
    expertSidebarForeground: "#ffffff",
    expertSidebarMuted: "rgba(255, 255, 255, 0.72)",
    expertDashboardCanvas: "#f6f6f6",
    expertNavBadge: "#7eb3e2",
    expertAvailable: "#22c55e",
    expertStatusActive: "#22c55e",
    expertStatusInactive: "#fdc700",

    expertActionGreen: "#16a34a",
    expertActionGreenHover: "#15803d",
    expertActionGreenSoft: "#ebfff2",
    expertActionGreenText: "#15803d",
    expertActionGreenRing: "rgba(22, 163, 74, 0.22)",

    expertActionBlue: "#51a2ff",
    expertActionBlueHover: "#3b82f6",
    expertActionBlueSoft: "#eef6ff",
    expertActionBlueText: "#1d4ed8",
    expertActionBlueRing: "rgba(81, 162, 255, 0.3)",

    expertActionAmber: "#fdc700",
    expertActionAmberHover: "#eab308",
    expertActionAmberSoft: "#fef9c2",
    expertActionAmberText: "#92400e",
    expertActionAmberRing: "rgba(253, 199, 0, 0.45)",

    expertActionOrange: "#fdc700",
    expertActionOrangeHover: "#eab308",

    expertBadgeNeutral: "#eef6ff",
    expertBadgeNeutralText: "#51a2ff",
    expertBadgeNeutralRing: "rgba(81, 162, 255, 0.25)",

    expertStatusDraftBg: "#f3f4f6",
    expertStatusDraftText: "#4b5563",
    expertStatusNewBg: "#fef9c2",
    expertStatusNewText: "#a16207",
    expertStatusDoneBg: "#ebfff2",
    expertStatusDoneText: "#15803d",
    expertStatusExpiredBg: "#fee2e2",
    expertStatusExpiredText: "#dc2626",

    expertError: "#dc2626",
    expertErrorSoft: "#fef2f2",

    expertDraftBanner: "#ebfff2",
    expertDraftBannerBorder: "#bbf7d0",
    expertDraftBannerText: "#15803d",
  },

  buttons: {
    sizes: {
      sm: {
        height: "1.75rem",
        paddingX: "1.75rem",
        fontSize: "0.75rem",
        fontWeight: "600",
        radius: "0.5rem",
        minWidth: "",
      },
      md: {
        height: "2.5rem",
        paddingX: "1rem",
        fontSize: "0.875rem",
        fontWeight: "600",
        radius: "0.5rem",
        minWidth: "8.5rem",
      },
      lg: {
        height: "auto",
        paddingX: "1rem",
        paddingY: "0.75rem",
        fontSize: "0.875rem",
        fontWeight: "600",
        radius: "9999px",
        minWidth: "",
      },
    },
    variants: {
      primary: {
        bg: "bg-primary",
        hover: "hover:bg-primary-hover",
        active: "active:bg-primary-active",
        text: "text-white",
        border: "",
      },
      secondary: {
        bg: "bg-secondary",
        hover: "hover:bg-secondary-hover",
        active: "active:bg-secondary-active",
        text: "text-white",
        border: "",
      },
      outline: {
        bg: "bg-white",
        hover: "hover:bg-primary/[0.04]",
        active: "",
        text: "text-primary",
        border: "border border-primary",
      },
      ghost: {
        bg: "bg-transparent",
        hover: "hover:bg-primary-soft",
        active: "",
        text: "text-primary",
        border: "",
      },
      success: {
        bg: "bg-expert-action-green",
        hover: "hover:bg-expert-action-green-hover",
        active: "",
        text: "text-white",
        border: "",
      },
      info: {
        bg: "bg-expert-action-blue",
        hover: "hover:bg-expert-action-blue-hover",
        active: "",
        text: "text-white",
        border: "",
      },
    },
  },

  icons: {
    nav: {
      sizePx: 24,
    },
    logo: {
      sizePx: 40,
      radius: "0.5rem",
    },
  },

  /** Evaluation / PDF report copy (brand colors still come from `colors`) */
  report: {
    subtitle: "Expert Portal",
    title: "Evaluation Report",
    layoutVersion: "v2" as "v1" | "v2",
    /**
     * Show the "Evaluated by Expert" card (name, stats, expertise) in the report
     * and PDF. Off for v1: not every expert agreed to show their details.
     */
    showExpertDetails: false,
  },
} as const;

export const themeConfig = {
  ...defaultThemeConfig,
  brand: {
    ...defaultThemeConfig.brand,
    name: appEnv.name || defaultThemeConfig.brand.name,
    appTitle: appEnv.title || defaultThemeConfig.brand.appTitle,
    logoSrc: appEnv.logoUrl || defaultThemeConfig.brand.logoSrc,
    logoAlt: appEnv.name || defaultThemeConfig.brand.logoAlt,
    faviconSrc: appEnv.faviconUrl || defaultThemeConfig.brand.faviconSrc,
    reportName: appEnv.reportName || defaultThemeConfig.brand.reportName,
  },
};

export type ThemeConfig = typeof defaultThemeConfig;
export type ButtonSize = keyof typeof defaultThemeConfig.buttons.sizes;
export type ButtonVariant = keyof typeof defaultThemeConfig.buttons.variants;
